import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

type Location = {
    latitude: number;
    longitude: number;
};

export function useGeolocation() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>(null);
    const { t } = useTranslation();

    const getLocation = useCallback((): Promise<Location> => {
        setLoading(true);
        setError(null);

        return new Promise(resolve => {
            if (!navigator.geolocation) {
                setLoading(false);
                setError(t("Geolocation not supported"));
                resolve(null);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                position => {
                    setLoading(false);
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                },
                err => {
                    setLoading(false);

                    switch (err.code) {
                        case err.PERMISSION_DENIED:
                            setError(t("Location permission denied"));
                            break;
                        case err.TIMEOUT:
                            setError(t("Location request timed out"));
                            break;
                        default:
                            setError(t("Unable to retrieve location"));
                    }

                    resolve(null);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0, // Don't use cached position. Pull real current position
                },
            );
        });
    }, []);

    return { getLocation, loading, error };
}